/**
 * CheckInConfig — Coach configures check-in schedule and questions per client.
 * Also shows recent check-in responses.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, Modal, TouchableOpacity,
  TextInput, ScrollView, Switch, Alert, KeyboardAvoidingView, Platform,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { X, Plus, Trash2, ClipboardCheck } from 'lucide-react-native';
import {
  type CheckInConfig as CheckInConfigType,
  type CheckInQuestion,
  type CheckInResponse,
  DEFAULT_QUESTIONS,
  loadCheckInResponses,
  getCheckInConfig,
  saveCheckInConfig,
  formatDays,
} from '@/lib/coachCheckIns';

const DAYS = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
];

interface Props {
  visible: boolean;
  coachId: string;
  clientId: string;
  clientName: string;
  onClose: () => void;
}

export default function CheckInConfigModal({ visible, coachId, clientId, clientName, onClose }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [config, setConfig] = useState<CheckInConfigType>({
    clientId,
    enabled: false,
    days: [1], // Monday default
    questions: [...DEFAULT_QUESTIONS],
  });
  const [responses, setResponses] = useState<CheckInResponse[]>([]);
  const [newQLabel, setNewQLabel] = useState('');
  const [newQType, setNewQType] = useState<'rating' | 'text' | 'yesno'>('text');
  const [tab, setTab] = useState<'config' | 'responses'>('config');

  const load = useCallback(async () => {
    const existing = await getCheckInConfig(coachId, clientId);
    if (existing) setConfig(existing);
    const resps = await loadCheckInResponses(coachId, clientId);
    setResponses(resps);
  }, [coachId, clientId]);

  useEffect(() => {
    if (visible) load();
  }, [visible, load]);

  const save = async (updated: CheckInConfigType) => {
    setConfig(updated);
    await saveCheckInConfig(coachId, updated);
  };

  const toggleDay = (day: number) => {
    const next = config.days.includes(day) ? config.days.filter((d) => d !== day) : [...config.days, day].sort();
    save({ ...config, days: next });
  };

  const addQuestion = () => {
    if (!newQLabel.trim()) return;
    const q: CheckInQuestion = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 4),
      label: newQLabel.trim(),
      type: newQType,
    };
    save({ ...config, questions: [...config.questions, q] });
    setNewQLabel('');
  };

  const removeQuestion = (id: string) => {
    save({ ...config, questions: config.questions.filter((q) => q.id !== id) });
  };

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: c.background, borderColor: c.border }]}>
          {/* Header */}
          <View style={styles.header}>
            <View>
              <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>CHECK-INS</Text>
              <Text style={{ color: c.textSecondary, fontSize: 12 }}>{clientName}</Text>
            </View>
            <TouchableOpacity onPress={onClose}><X size={22} color={c.textSecondary} /></TouchableOpacity>
          </View>

          {/* Tabs */}
          <View style={[styles.tabRow, { borderColor: c.border }]}>
            <TouchableOpacity
              style={[styles.tab, tab === 'config' && { borderBottomColor: c.accent, borderBottomWidth: 2 }]}
              onPress={() => setTab('config')}
            >
              <Text style={{ color: tab === 'config' ? c.accent : c.textSecondary, fontSize: 13, fontWeight: '700' }}>Configure</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, tab === 'responses' && { borderBottomColor: c.accent, borderBottomWidth: 2 }]}
              onPress={() => setTab('responses')}
            >
              <Text style={{ color: tab === 'responses' ? c.accent : c.textSecondary, fontSize: 13, fontWeight: '700' }}>
                Responses ({responses.length})
              </Text>
            </TouchableOpacity>
          </View>

          <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 20 }}>
            {tab === 'config' ? (
              <>
                {/* Enable toggle */}
                <View style={[styles.row, { borderColor: c.border }]}>
                  <Text style={[styles.rowLabel, { color: c.text }]}>Enable Check-ins</Text>
                  <Switch
                    value={config.enabled}
                    onValueChange={(v) => save({ ...config, enabled: v })}
                    trackColor={{ true: c.accent }}
                  />
                </View>

                {/* Day selector */}
                <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>SCHEDULE</Text>
                <View style={styles.dayRow}>
                  {DAYS.map((d) => {
                    const active = config.days.includes(d.value);
                    return (
                      <TouchableOpacity
                        key={d.value}
                        style={[styles.dayBtn, { backgroundColor: active ? c.accent : c.surface, borderColor: c.border }]}
                        onPress={() => toggleDay(d.value)}
                      >
                        <Text style={{ color: active ? '#fff' : c.textSecondary, fontSize: 12, fontWeight: '700' }}>{d.label}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {/* Questions */}
                <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>QUESTIONS</Text>
                {config.questions.map((q) => (
                  <View key={q.id} style={[styles.questionRow, { borderColor: c.border }]}>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.qLabel, { color: c.text }]}>{q.label}</Text>
                      <Text style={{ color: c.textTertiary, fontSize: 11 }}>{q.type}</Text>
                    </View>
                    <TouchableOpacity onPress={() => removeQuestion(q.id)}>
                      <Trash2 size={14} color={c.textTertiary} />
                    </TouchableOpacity>
                  </View>
                ))}

                {/* Add question */}
                <View style={{ marginTop: 12, gap: 8 }}>
                  <TextInput
                    style={[styles.input, { color: c.text, backgroundColor: c.surface, borderColor: c.border }]}
                    placeholder="New question..."
                    placeholderTextColor={c.textTertiary}
                    value={newQLabel}
                    onChangeText={setNewQLabel}
                    maxLength={100}
                  />
                  <View style={{ flexDirection: 'row', gap: 6 }}>
                    {(['rating', 'text', 'yesno'] as const).map((t) => (
                      <TouchableOpacity
                        key={t}
                        style={[styles.typeBtn, { backgroundColor: newQType === t ? c.accent : c.surface, borderColor: c.border }]}
                        onPress={() => setNewQType(t)}
                      >
                        <Text style={{ color: newQType === t ? '#fff' : c.textSecondary, fontSize: 11, fontWeight: '700' }}>
                          {t === 'yesno' ? 'Yes/No' : t.charAt(0).toUpperCase() + t.slice(1)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                  <TouchableOpacity
                    style={[styles.addBtn, { backgroundColor: newQLabel.trim() ? c.accent : c.border }]}
                    onPress={addQuestion}
                    disabled={!newQLabel.trim()}
                  >
                    <Plus size={14} color="#fff" />
                    <Text style={{ color: '#fff', fontSize: 13, fontWeight: '700' }}>Add Question</Text>
                  </TouchableOpacity>
                </View>
              </>
            ) : (
              /* Responses tab */
              <>
                {responses.length === 0 ? (
                  <Text style={{ color: c.textTertiary, textAlign: 'center', marginTop: 40, fontSize: 14 }}>
                    No check-in responses yet.
                  </Text>
                ) : (
                  responses.map((r) => (
                    <View key={r.id} style={[styles.responseCard, { backgroundColor: c.surface, borderColor: c.border }]}>
                      <Text style={[styles.responseDate, { color: c.textSecondary }]}>
                        {new Date(r.submitted_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        {' '}
                        {new Date(r.submitted_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </Text>
                      {Object.entries(r.answers).map(([key, val]) => {
                        const q = config.questions.find((qq) => qq.id === key);
                        return (
                          <View key={key} style={styles.answerRow}>
                            <Text style={[styles.answerLabel, { color: c.textSecondary }]}>
                              {q?.label ?? key}
                            </Text>
                            <Text style={[styles.answerValue, { color: c.text }]}>
                              {typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val)}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  ))
                )}
              </>
            )}
          </ScrollView>
          </KeyboardAvoidingView>
        </View>
      </View>
    </Modal>
  );
}

/* ── Inline check-in card for client detail ── */
export function CheckInSummary({ coachId, clientId }: { coachId: string; clientId: string }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [config, setConfig] = useState<CheckInConfigType | null>(null);
  const [lastResponse, setLastResponse] = useState<CheckInResponse | null>(null);

  useEffect(() => {
    (async () => {
      const cfg = await getCheckInConfig(coachId, clientId);
      setConfig(cfg);
      const resps = await loadCheckInResponses(coachId, clientId, 1);
      if (resps.length > 0) setLastResponse(resps[0]);
    })();
  }, [coachId, clientId]);

  if (!config || !config.enabled) return null;

  return (
    <View style={[summaryStyles.container, { backgroundColor: c.surface, borderColor: c.border }]}>
      <View style={summaryStyles.headerRow}>
        <ClipboardCheck size={16} color={c.accent} />
        <Text style={[summaryStyles.title, { color: c.text, fontFamily: TYPO.heading.semibold }]}>CHECK-INS</Text>
      </View>
      <Text style={{ color: c.textSecondary, fontSize: 12 }}>
        Schedule: {formatDays(config.days)} · {config.questions.length} questions
      </Text>
      {lastResponse ? (
        <Text style={{ color: c.textTertiary, fontSize: 11, marginTop: 4 }}>
          Last response: {new Date(lastResponse.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </Text>
      ) : (
        <Text style={{ color: c.textTertiary, fontSize: 11, marginTop: 4 }}>No responses yet</Text>
      )}
    </View>
  );
}

const summaryStyles = StyleSheet.create({
  container: { borderWidth: 1, borderRadius: 2, padding: 16, marginBottom: 12 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  title: { flex: 1, fontSize: 13, letterSpacing: 1 },
});

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'flex-end' },
  modal: { borderTopLeftRadius: 2, borderTopRightRadius: 2, borderWidth: 1, borderBottomWidth: 0, maxHeight: '90%', minHeight: '60%', padding: 16 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  title: { fontSize: 16, fontWeight: '900', letterSpacing: 1 },
  tabRow: { flexDirection: 'row', borderBottomWidth: 1, marginBottom: 12 },
  tab: { flex: 1, alignItems: 'center', paddingVertical: 8 },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, borderBottomWidth: StyleSheet.hairlineWidth },
  rowLabel: { fontSize: 14, fontWeight: '600' },
  sectionLabel: { fontSize: 12, fontWeight: '800', letterSpacing: 1, marginTop: 16, marginBottom: 8 },
  dayRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  dayBtn: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 2, borderWidth: 1 },
  questionRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, borderBottomWidth: StyleSheet.hairlineWidth },
  qLabel: { fontSize: 14, fontWeight: '500' },
  input: { borderWidth: 1, borderRadius: 2, padding: 10, fontSize: 14 },
  typeBtn: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 2, borderWidth: 1 },
  addBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 2, paddingVertical: 10 },
  responseCard: { borderWidth: 1, borderRadius: 2, padding: 12, marginBottom: 10 },
  responseDate: { fontSize: 12, fontWeight: '700', marginBottom: 8 },
  answerRow: { marginBottom: 4 },
  answerLabel: { fontSize: 11, fontWeight: '600' },
  answerValue: { fontSize: 14, fontWeight: '500' },
});
